import React from 'react';
import { _T, C } from '../../shared/theme';

/**
 * High-tech indeterminate loading bar that matches the active theme.
 * props:
 *   label  — string shown below the bar (optional)
 *   lang   — 'nl' | 'en'
 */
function TechLoadingBar({ label, lang }) {
  const nl = lang === 'nl';
  const defaultLabel = nl ? 'GEGEVENS OPHALEN' : 'SCANNING DATA';
  const text = label || defaultLabel;

  return (
    <div style={{ padding: '32px 0 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>

      {/* Bar */}
      <div style={{ width: '100%', position: 'relative' }}>
        {/* Top grid lines — give a HUD feel */}
        <div style={{
          position: 'absolute', top: -14, left: 0, right: 0,
          display: 'flex', justifyContent: 'space-between', pointerEvents: 'none',
        }}>
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} style={{
              width: 1, height: 8,
              background: i === 0 || i === 8 ? `${_T.accent}60` : `${_T.accent}25`,
            }} />
          ))}
        </div>

        {/* Track */}
        <div style={{
          width: '100%', height: 3,
          background: `${_T.accent}18`,
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: `0 0 0 1px ${_T.accent}12`,
        }}>
          {/* Sweeping fill */}
          <div style={{
            position: 'absolute', top: 0, height: '100%',
            background: `linear-gradient(90deg, transparent, ${_T.grad1}, ${_T.grad2}, ${_T.accent}, transparent)`,
            animation: 'techScan 1.5s cubic-bezier(0.4,0,0.6,1) infinite',
            borderRadius: 2,
            boxShadow: `0 0 10px 2px ${_T.accent}90, 0 0 22px 4px ${_T.grad1}50`,
          }} />
        </div>

        {/* Bottom scan corners */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <div style={{ width: 6, height: 1, background: `${_T.accent}50` }} />
          <div style={{ width: 6, height: 1, background: `${_T.accent}50` }} />
        </div>
      </div>

      {/* Label + blinking cursor + dots */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Hex icon */}
          <span style={{
            fontSize: 10, color: _T.accent, fontWeight: 700, letterSpacing: 1,
            animation: 'techGlow 2s ease-in-out infinite',
          }}>⬡</span>

          <span style={{
            fontFamily: 'Inter, monospace', fontSize: 10, letterSpacing: 3,
            color: `${_T.accent}cc`, fontWeight: 700,
          }}>
            {text}
          </span>

          {/* Blinking cursor */}
          <span style={{
            display: 'inline-block', width: 6, height: 10,
            background: _T.accent,
            borderRadius: 1,
            animation: 'techBlink 1s step-end infinite',
            marginLeft: 2,
          }} />
        </div>

        {/* Animated equaliser bars */}
        <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 14 }}>
          {[0.6, 1.0, 0.3, 0.8, 0.5, 1.0, 0.4, 0.7, 0.3, 0.9, 0.5, 0.6].map((delay, i) => (
            <div key={i} style={{
              width: 2, height: 14,
              background: `linear-gradient(180deg, ${_T.grad2}, ${_T.grad1})`,
              borderRadius: 1,
              animation: `techDot ${0.7 + delay * 0.4}s ease-in-out ${delay * 0.15}s infinite`,
              opacity: 0.7,
            }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default TechLoadingBar;
